import React, { useState, useEffect, useMemo, useRef } from 'react';
import type Konva from 'konva';
import { useStore, useStorePrimitive, useActions } from './useStore';
import { useCreateRegionModal } from './useModal';
import { DrawingType } from '../types/drawing-types';
import type { InteractionState, MapRegion, RenderedImageRect, RegionFormData } from '../types/types';
import { logger } from '../services/logger.client';
import type { GridSettings } from '../components/GridProvider/useGrid';
import { useStableCallback } from './hookUtils';

interface UseMapInteractionProps {
  drawingMode: string | null; // 'region', 'region-click', 'line', etc.
  setDrawingMode: (mode: string | null) => void;
  stageRef: React.RefObject<Konva.Stage | null>;
  imageRect: RenderedImageRect | null;
  gridSettings: GridSettings; // from useGridCalculator
  getCoordsInPercent: (p: { x: number; y: number }) => { x: number; y: number };
}


export interface UseMapInteractionReturn {
  interaction: InteractionState | null;
  hoveredCell: { row: number; col: number } | null;
  setHoveredCell: React.Dispatch<React.SetStateAction<{ row: number; col: number; } | null>>;
  tooltipState: { x: number; y: number; visible: boolean; cell: { row: number; col: number } | null };
  setTooltipState: React.Dispatch<React.SetStateAction<{ x: number; y: number; visible: boolean; cell: { row: number; col: number } | null }>>;
}

export const useMapInteraction = ({
  drawingMode,
  setDrawingMode,
  imageRect,
  gridSettings,
  getCoordsInPercent,
}: UseMapInteractionProps) => {
  // Global state actions
  const activeMapId = useStorePrimitive(state => state.session?.map?.activeMapId);
  const mapRegions = useStore(state => state.session?.map?.mapRegions);
  const { show: showCreateRegionModal } = useCreateRegionModal();
  const actions = useActions();

  // Local state
  const [interaction, setInteraction] = useState<InteractionState | null>(null);
  const [hoveredCell, setHoveredCell] = useState<{ row: number; col: number } | null>(null);
  const isAltPressedRef = useRef(false);
  const lastMousePositionRef = useRef({ x: 0, y: 0 });
  const [tooltipState, setTooltipState] = useState<{ x: number, y: number, visible: boolean, cell: { row: number, col: number } | null }>({ x: 0, y: 0, visible: false, cell: null });

  const occupiedCells = useMemo(() => {
    const cellSet = new Set<string>();
    (mapRegions ?? [])
      .filter((region: MapRegion) => region.mapId === activeMapId)
      .forEach((region: MapRegion) => {
        ((region.cells as { row: number, col: number }[]) ?? []).forEach((cell) => {
          cellSet.add(`${cell.row}-${cell.col}`);
        });
      });
    return cellSet;
  }, [mapRegions, activeMapId]);

  // useStableCallback provides a stable reference while always having access to
  // the latest state values. This prevents the useEffect dependency array from
  // triggering unnecessary re-subscriptions to keyboard events.
  const saveRegion = useStableCallback(() => {
    if (interaction?.type !== 'region' || interaction.selectedCells.size === 0 || activeMapId == null) return;
    logger.debug({ interaction }, 'Saving region, opening create region modal.');
    showCreateRegionModal((formData: RegionFormData) => {
      const regionCells = Array.from(interaction.selectedCells).map((cellKey) => {
        const [row, col] = cellKey.split('-').map(Number);
        return { row, col };
      });
      const newRegion: Omit<MapRegion, 'id'> = {
        name: formData.name, mapId: activeMapId,
        type: gridSettings.type, cells: regionCells, visible: true, color: formData.color,
        opacity: formData.opacity / 100, terrainTypeId: formData.terrainTypeId ? Number(formData.terrainTypeId) : null, createdAt: new Date(), updatedAt: new Date()
      } as Omit<MapRegion, 'id'>;
      actions.addMapRegion(newRegion);
      setInteraction(null);
      setDrawingMode(null);
    });
  });

  // useStableCallback ensures finishDrawing has a stable reference for the
  // keyboard event listener useEffect, while always accessing latest state.
  const finishDrawing = useStableCallback(() => {
    if (!interaction || !imageRect || activeMapId == null) return;

    if (interaction.type === 'line' && interaction.points.length >= 2) {
      const pointsInPercent = (interaction.points ?? []).map(getCoordsInPercent);
      if (drawingMode === 'journey') {
        const days = parseInt(prompt('Enter number of days for the journey (2-20):', '7') ?? '0', 10);
        if (isNaN(days) || days < 2 || days > 20) return;
        const name = prompt('Enter a name for this journey:', 'The Long Road');
        if (name) actions.addMapDrawing({ name, type: DrawingType.journey, color: '#3399ff', geometry: { points: pointsInPercent },
          days, visible: true, mapId: activeMapId, createdAt: new Date(), updatedAt: new Date(),
        });
      } else { // line
        const name = prompt('Enter a name for this drawing:', 'New Path');
        if (name) actions.addMapDrawing({ name, type: DrawingType.line, color: '#ff3333', geometry: { points: pointsInPercent },
          visible: true, mapId: activeMapId, days: null, createdAt: new Date(), updatedAt: new Date(),
        });
      }
    } else if (interaction.type === 'grid-path') {
      const name = prompt('Enter a name for this path:', 'New Grid Path');
      if (name) actions.addMapDrawing({ name, type: DrawingType.grid_path, color: '#39e639',
        geometry: { points: [getCoordsInPercent(interaction.start), getCoordsInPercent(interaction.current)] }, visible: true, mapId: activeMapId, days: null, createdAt: new Date(), updatedAt: new Date(),
      });
    } else if (interaction.type === 'cone') {
      const name = prompt('Enter a name for this cone effect:', 'Burning Hands');
      if (name) actions.addMapDrawing({ name, type: DrawingType.cone, color: '#ff4d4f',
        geometry: { points: [getCoordsInPercent(interaction.start), getCoordsInPercent(interaction.current)] }, visible: true, mapId: activeMapId, days: null, createdAt: new Date(), updatedAt: new Date(),
      });
    } else if (interaction.type === 'shape') {
      const name = prompt(`Enter a name for this ${interaction.shape}:`, `New ${interaction.shape}`);
      if (name) {
        const startPercent = getCoordsInPercent(interaction.start);
        const currentPercent = getCoordsInPercent(interaction.current);
        const dx = Math.abs(startPercent.x - currentPercent.x);
        const dy = Math.abs(startPercent.y - currentPercent.y);
        const geometry = {
          posX: Math.min(startPercent.x, currentPercent.x),
          posY: Math.min(startPercent.y, currentPercent.y),
          width: dx,
          height: dy,
          cx: startPercent.x,
          cy: startPercent.y,
          radius: Math.sqrt(dx * dx + dy * dy),
        };
        actions.addMapDrawing({ name, type: interaction.shape, color: '#ff9900', visible: true, mapId: activeMapId,
          geometry: geometry, days: null, createdAt: new Date(), updatedAt: new Date(),
        });
      }
    }
    setInteraction(null);
    setDrawingMode(null);
  });

  // useStableCallback ensures cancelDrawing has a stable reference for the
  // keyboard event listener useEffect, avoiding re-subscription on every render.
  const cancelDrawing = useStableCallback(() => {
    setInteraction(null);
    setDrawingMode(null);
  });

  // Effect to detect when region drawing is completed via button toggle.
  // saveRegion is a stable callback, so its inclusion doesn't cause extra runs.
  // We intentionally depend on drawingMode and interaction to detect state transitions.
  useEffect(() => {
    // If the drawing mode was 'region-click' and is now null, and we have a region interaction...
    if (drawingMode === null && interaction?.type === 'region' && interaction.selectedCells.size > 0) {
      // Check if the last interaction was likely a click-region one. A bit of a heuristic.
      logger.debug({ interaction }, 'Region click-drawing finished via button toggle, opening save modal.');
      saveRegion();
    }
  }, [drawingMode, interaction, saveRegion]);

  // Keyboard event handlers for drawing mode interactions.
  // finishDrawing and cancelDrawing are stable callbacks (via useStableCallback),
  // so including them in deps doesn't cause re-subscriptions on every render.
  // drawingMode is intentionally included to update the handler behavior when mode changes.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Alt') {
        e.preventDefault();
        isAltPressedRef.current = true;
        // Manually update tooltip visibility without causing a re-render of the parent
        const { x, y } = lastMousePositionRef.current;
        setTooltipState(s => ({ ...s, visible: true, x, y }));
      }
      if (drawingMode) {
        if (e.key === 'Enter') finishDrawing();
        if (e.key === 'Escape') cancelDrawing();
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Alt') {
        isAltPressedRef.current = false;
        setTooltipState(s => ({ ...s, visible: false }));
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [drawingMode, finishDrawing, cancelDrawing]);

  return {
    interaction,
    hoveredCell,
    setHoveredCell,
    tooltipState,
    setTooltipState,
    saveRegion,
    setInteraction,
    lastMousePositionRef,
    occupiedCells,
    finishDrawing,
  };
};